import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useAppDispatch } from "../../../store";
import { setCurrentPage } from "../../../store/auth/authReducer";
import Button from "@mui/material/Button";
import { Box, FormControl, InputLabel, MenuItem, Select, TextField, Typography, Stack, Switch, Card, CardContent, Chip, Paper, Tooltip, IconButton, Collapse, Alert, Popover } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import InboxIcon from '@mui/icons-material/Inbox';
import CodeIcon from "@mui/icons-material/Code";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import BarChartIcon from "@mui/icons-material/BarChart";
import HistoryIcon from "@mui/icons-material/History";
import axios from "axios";
import { logOut } from "../../../utils/logOut";
import { styled, width } from "@mui/system";
import "./ConversationAnalytics.css";
import TranslatedText from "../../../utils/Translate";
import { useSnackbar } from "../../../utils/SnackbarContext";
import HtmlInShadow from "../../../utils/HtmlInShadow";
import NoDataOverlay from "../../../utils/NoDataOverlay";
import Markdown from "../../../utils/Markdown";
import { Link } from "react-router-dom";
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

// ==========================
// Types
// ==========================

type HistoryMessage = { role: "system" | "user" | "assistant"; content: string };

type BlockType = "user_prompt" | "function_call" | "function_result" | "usage" | "a_history";

interface BlockData {
  type: BlockType;
  content: string;
  functionName?: string;
  timestamp: string;
  id: string;
}

type AIModel = { id: string; name: string };

// ==========================
// Styled Components
// ==========================

const ChatContainer = styled("div")({
  display: "flex",
  width: "90%",
  minWidth: "800px",
  maxWidth: "1800px",
  flexDirection: "column",
  height: "100%",
  minHeight: 0,
});
const MainContainer = styled("div")({
  display: "flex",
  flexDirection: "row",
  gap: 16,
  flex: 1,
  minHeight: 0,
});
const ChatSection = styled("div")({
  flex: 2,
  display: "flex",
  flexDirection: "column",
  minWidth: "400px",
  minHeight: 0,
});

const COLLAPSED_W = 70;

const BlocksSection = styled("div")<{ collapsed: boolean }>(({ collapsed, theme }) => ({
  display: "flex",
  flexDirection: "column",
  height: "100%",
  minHeight: 0,
  minWidth: collapsed ? `${COLLAPSED_W}px` : "300px",
  maxWidth: collapsed ? `${COLLAPSED_W}px` : "500px",
  width: collapsed ? `${COLLAPSED_W}px` : "500px",
  position: "relative",
  overflowY: "auto",
  backgroundColor: theme.palette.grey[50],
  borderRadius: 3,
}));

const BlocksContainer = styled(Paper)(({ theme }) => ({
  padding: 16,
  flex: 1,
  backgroundColor: theme.palette.grey[50],
  "& > .va-anim": {
    opacity: 0,
    transform: "translateY(8px)",
    transition: "opacity 300ms ease, transform 300ms ease",
  },
  "& > .va-anim.va-in": {
    opacity: 1,
    transform: "translateY(0)",
  },
}));

const colorForBlockType = (t: BlockType, theme: any) => {
  switch (t) {
    case "user_prompt": return theme.palette.warning.main;
    case "function_call": return theme.palette.info.main;
    case "function_result": return theme.palette.success.main;
    case "usage": return theme.palette.primary.main;
    case "a_history": return theme.palette.secondary.main;
    default: return theme.palette.text.primary;
  }
};
const BlocksHeader = styled(Box)<{ collapsed: boolean }>(({ theme, collapsed }) => ({
  display: "flex",
  alignItems: "center",
  gap: 8,
  justifyContent: collapsed ? "center" : "space-between",
}));
const CollapsedBar = styled(Paper)(({ theme }) => ({
  width: COLLAPSED_W,
  height: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  padding: 8,
  gap: 8,
  overflowY: "auto",
  backgroundColor: theme.palette.grey[50],
}));
const ChatMessagesRoot = styled("div")({
  padding: "0 20px",
  flex: 1,
  overflowY: "auto",
  minHeight: 0,
  "& > *": {
    opacity: 0,
    transition: "opacity 0.3s ease-in-out",
  },
});
const MessageContainer = styled("div")({
  marginBottom: 16,
  "&.scroll-animation": { opacity: 1, transform: "translateY(0)" },
});
const InputContainer = styled("div")({
  display: "flex",
  alignItems: "center",
  padding: 16,
  gap: 12,
});
const BlockCard = styled(Card)(({ theme }) => ({
  marginBottom: "12px",
  borderLeft: `4px solid ${theme.palette.primary.main}`,
}));
const FunctionCallCard = styled(BlockCard)(({ theme }) => ({
  borderLeft: `4px solid ${theme.palette.info.main}`,
}));
const FunctionResultCard = styled(BlockCard)(({ theme }) => ({
  borderLeft: `4px solid ${theme.palette.success.main}`,
}));
const UserPromptCard = styled(BlockCard)(({ theme }) => ({
  borderLeft: `4px solid ${theme.palette.warning.main}`,
}));
const UsageCard = styled(BlockCard)(({ theme }) => ({
  borderLeft: `4px solid ${theme.palette.primary.main}`,
}));
const AHistoryCard = styled(BlockCard)(({ theme }) => ({
  borderLeft: `4px solid ${theme.palette.secondary.main}`,
}));

// ==========================
// Component
// ==========================

function MemoChatMessages(props: {
  messages: { id: number; text: string; sender: "user" | "bot" }[];
  parseHTML: boolean;
  chatRef: React.RefObject<HTMLDivElement>;
}) {
  const { messages, parseHTML, chatRef } = props;
  return (
    <ChatMessagesRoot ref={chatRef}>
      {messages.length === 0 ? (
        <Box sx={{ opacity: 1, height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <NoDataOverlay text="No messages yet" />
        </Box>
      ) : (
        messages.map((message, idx) => (
          <MessageContainer key={idx} className="scroll-animation" style={{ display: "flex", flexDirection: "column", alignItems: message.sender === "user" ? "flex-end" : "flex-start" }}>
            <Typography variant="caption" sx={{ mb: 0.5, color: "text.secondary", alignSelf: message.sender === "user" ? "flex-end" : "flex-start" }}>
              {message.sender === "user" ? "You" : "Bot"}
            </Typography>
            <Box sx={{ maxWidth: "70%", p: 1.5, borderRadius: 3, bgcolor: message.sender === "user" ? "primary.main" : "grey.100", color: message.sender === "user" ? "white" : "text.primary", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
              {parseHTML ? (<HtmlInShadow html={message.text} />) : (<Markdown text={message.text} />)}
            </Box>
          </MessageContainer>
        ))
      )}
    </ChatMessagesRoot>
  );
}

export default function ConversationAnalyticsSpeech() {
  const dispatch = useAppDispatch();
  const { showSnackbar } = useSnackbar();

  // Маркер страницы
  useEffect(() => { dispatch(setCurrentPage(`Conversation Analytics`)); }, [dispatch]);

  // --- Хранилище: меняем ключи, чтобы не пересекаться с другими модулями ---
  const LS_CFG_KEY = "sa_config";                   // было: va_config
  const LS_HIST_KEY = "sa_history";                 // было: va_history
  const LS_BLOCKS_COLLAPSED_KEY = "sa_blocks_collapsed"; // было: va_blocks_collapsed

  // Стейты
  const [aimodels, setAImodels] = useState<AIModel[]>([]);
  const [aiprompts, setAIprompts] = useState<any[]>([]);
  const chatRef = useRef<HTMLDivElement | null>(null);
  const blocksRef = useRef<HTMLDivElement | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [parseHTML, setParseHTML] = useState(false);
  const [blocks, setBlocks] = useState<BlockData[]>([]);
  const [prompt, setPrompt] = useState<string>("");

  // История чата (для отображения и отправки в history_b)
  const [chatHistory, setChatHistory] = useState<HistoryMessage[]>(() => {
    const savedHistory = localStorage.getItem(LS_HIST_KEY) || "[]";
    try { return JSON.parse(savedHistory); } catch { return []; }
  });

  const [chatConfig, setChatConfig] = useState<{
    aimodel: string;
    aiprompt_a?: string;
    aiprompt_b?: string;
    mode: "message" | "stream";
  }>(() => {
    const cfg = localStorage.getItem(LS_CFG_KEY);
    if (cfg) {
      const json = JSON.parse(cfg);
      return {
        aimodel: json.aimodel || "",
        aiprompt_a: json.aiprompt_a || "",
        aiprompt_b: json.aiprompt_b || "",
        mode: "stream",
      };
    }
    return { aimodel: "", aiprompt_a: "", aiprompt_b: "", mode: "stream" };
  });

  const [isBlocksCollapsed, setIsBlocksCollapsed] = useState<boolean>(() => {
    const saved = localStorage.getItem(LS_BLOCKS_COLLAPSED_KEY);
    return saved ? saved === "1" : true;
  });

  const [anchorElConversations, setAnchorElConversations] = useState<HTMLElement | null>(null);
  const handleOpenConversations = (e: React.MouseEvent<HTMLElement>) => setAnchorElConversations(e.currentTarget);
  const handleCloseConversations = () => setAnchorElConversations(null);

  const saveConfig = useCallback(() => {
    localStorage.setItem(LS_CFG_KEY, JSON.stringify(chatConfig));
  }, [chatConfig]);

  useEffect(() => { localStorage.setItem(LS_HIST_KEY, JSON.stringify(chatHistory)); }, [chatHistory]);
  useEffect(() => { localStorage.setItem(LS_BLOCKS_COLLAPSED_KEY, isBlocksCollapsed ? "1" : "0"); }, [isBlocksCollapsed]);
  useEffect(() => {
    if (!localStorage.getItem(LS_CFG_KEY) && aimodels.length > 0) {
      setChatConfig(prev => ({ ...prev, aimodel: aimodels[0].name }));
    }
  }, [aimodels]);

  // Автоскролл чата и блоков
  useEffect(() => { if (chatRef.current) chatRef.current.scrollTop = chatRef.current.scrollHeight; }, [chatHistory]);
  useEffect(() => { if (blocksRef.current) blocksRef.current.scrollTop = blocksRef.current.scrollHeight; }, [blocks]);

  // Мемо для сообщений
  const messages = useMemo(() =>
    chatHistory
      .filter((item) => item.role === "user" || item.role === "assistant")
      .map((item, idx) => ({ id: idx, text: item.content, sender: item.role === "user" ? "user" : "bot" as "user" | "bot" })),
    [chatHistory]
  );

  // Иконки для блоков
  const iconForBlockType = (t: BlockType) => {
    switch (t) {
      case "user_prompt": return <ChatBubbleOutlineIcon fontSize="small" />;
      case "function_call": return <CodeIcon fontSize="small" />;
      case "function_result": return <CheckCircleOutlineIcon fontSize="small" />;
      case "usage": return <BarChartIcon fontSize="small" />;
      case "a_history": return <HistoryIcon fontSize="small" />;
      default: return <ChatBubbleOutlineIcon fontSize="small" />;
    }
  };

  // Handlers
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => setPrompt(e.target.value);
  const handleClearHistory = () => {
    setChatHistory([]);
    setBlocks([]);
    localStorage.removeItem(LS_HIST_KEY);
  };

  // JSON pretty printer для блоков
  const formatJson = (content: string): string => {
    try {
      const maybe = JSON.parse(content);
      if (typeof maybe === "string") {
        try { return JSON.stringify(JSON.parse(maybe), null, 2); } catch { /* noop */ }
      }
      return JSON.stringify(maybe, null, 2);
    } catch {
      return content;
    }
  };

  // Анимация появления блоков
  const appearedIdsRef = React.useRef<Set<string>>(new Set());
  function Appear({ id, children, delay = 0 }: { id: string; children: React.ReactNode; delay?: number; }) {
    const ref = React.useRef<HTMLDivElement | null>(null);
    const alreadyAppeared = appearedIdsRef.current.has(id);
    React.useEffect(() => {
      if (alreadyAppeared) return;
      const el = ref.current; if (!el) return;
      el.style.transitionDelay = `${delay}ms`;
      const raf = requestAnimationFrame(() => {
        el.classList.add("va-in");
        appearedIdsRef.current.add(id);
      });
      return () => cancelAnimationFrame(raf);
    }, [id, delay, alreadyAppeared]);
    const className = alreadyAppeared ? undefined : "va-anim";
    return (<div ref={ref} className={className} style={{ marginBottom: 12 }}>{children}</div>);
  }

  // ---------------------------
  // STREAM SEND (Speech Analytics)
  // ---------------------------
  const sendStreamMessage = async (userPrompt: string) => {
    if (abortRef.current) { try { abortRef.current.abort(); } catch { /* noop */ } }
    const controller = new AbortController();
    abortRef.current = controller;
    setIsStreaming(true);
    setBlocks([]);

    // Покажем текст пользовательского задания в чате (если непустой)
    if (userPrompt) {
      setChatHistory(prev => [...prev, { role: "user", content: userPrompt }]);
    }

    // conversations берём из localStorage.remembered_conversations
    let conversations: string[] = [];
    try {
      const raw = localStorage.getItem("remembered_conversations") || "[]";
      const arr = JSON.parse(raw);
      if (Array.isArray(arr)) conversations = arr.map(String);
    } catch { /* noop */ }

    // ВАЖНО: соберём history_b синхронно (не полагаясь на setState)
    const requestHistoryB: HistoryMessage[] = userPrompt
      ? [...chatHistory, { role: "user", content: userPrompt }]
      : [...chatHistory];

    try {
      const resp = await fetch("https://cardgarantsales.com/restapi/conversationanalytics", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + localStorage.getItem("access_token"),
        },
        body: JSON.stringify([{
          aimodel: chatConfig.aimodel,
          conversations,
          aiprompt_a: chatConfig.aiprompt_a ? Number(chatConfig.aiprompt_a) : 0,
          aiprompt_b: chatConfig.aiprompt_b ? Number(chatConfig.aiprompt_b) : 0,
          prompt: userPrompt, // может быть пустым
          include_user_prompt: true,
          include_function_calls: true,
          include_function_outputs: true,
          history_b: requestHistoryB, // <--- отправляем историю в фазу B
          mode: "stream",
        }]),
        signal: controller.signal,
      });
      if (!resp.ok || !resp.body) throw new Error(`Stream error: ${resp.status}`);

      const reader = resp.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let buf = "";

      // Текущий открытый блок
      let currentBlock: BlockData | null = null;
      let blockContent = "";

      const appendAssistantDelta = (delta: string) => {
        setChatHistory((prev) => {
          const hist = [...prev];
          if (hist.length === 0 || hist[hist.length - 1].role !== "assistant") {
            hist.push({ role: "assistant", content: "" });
          }
          const last = hist[hist.length - 1];
          hist[hist.length - 1] = { ...last, content: (last.content || "") + delta };
          return hist;
        });
      };

      // SSE разборщик (event:/data:)
      let evtType: string | null = null;
      let evtDataChunks: string[] = [];

      const flushEvent = () => {
        if (!evtType && evtDataChunks.length === 0) return;
        const dataStr = evtDataChunks.join("\n");
        evtDataChunks = [];

        if (evtType === "done") { evtType = null; return; }

        if (evtType === "block_end" && currentBlock) {
          const finalized: BlockData = { ...currentBlock, content: blockContent, timestamp: new Date().toLocaleTimeString(), };
          setBlocks(prev => [...prev, finalized]);
          currentBlock = null; blockContent = ""; evtType = null; return;
        }

        if (evtType === "block_start") {
          try {
            const meta = JSON.parse(dataStr);
            currentBlock = {
              type: meta.block_type as BlockType,
              content: "",
              functionName: meta.function_name,
              timestamp: new Date().toLocaleTimeString(),
              id: Math.random().toString(36).slice(2),
            };
            blockContent = "";
          } catch { /* noop */ }
          evtType = null; return;
        }

        // Спец. событие истории аудита (фаза A)
        if (evtType === "a_history") {
          setBlocks(prev => [...prev, {
            type: "a_history",
            content: dataStr,
            timestamp: new Date().toLocaleTimeString(),
            id: Math.random().toString(36).slice(2),
          }]);
          evtType = null; return;
        }

        // usage (статистика токенов)
        try {
          const maybe = JSON.parse(dataStr);
          if (maybe && typeof maybe === "object" && "completion_tokens" in maybe) {
            setBlocks(prev => [...prev, { type: "usage", content: JSON.stringify(maybe, null, 2), timestamp: new Date().toLocaleTimeString(), id: Math.random().toString(36).slice(2), }]);
            evtType = null; return;
          }
        } catch { /* noop */ }

        // Обычные дельты контента
        const handleDelta = (d: string) => {
          if (!d) return;
          if (currentBlock) { blockContent += d; }
          else { appendAssistantDelta(d); }
        };

        if (dataStr.trim() === "{}" || dataStr.trim() === "[DONE]") { evtType = null; return; }

        const lines = dataStr.split(/\r?\n/).filter((l) => l.length > 0);
        let parsedAny = false;
        for (const l of lines) {
          try {
            const obj = JSON.parse(l);
            if (obj && typeof obj === "object" && "delta" in obj) {
              handleDelta(String(obj.delta));
              parsedAny = true;
            }
          } catch { /* noop */ }
        }
        if (!parsedAny) handleDelta(dataStr);
        evtType = null;
      };

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split(/\r?\n/);
        buf = lines.pop() || "";
        for (const line of lines) {
          if (line.startsWith("event: ")) { flushEvent(); evtType = line.slice(7).trim(); continue; }
          if (line.startsWith("data:")) { evtDataChunks.push(line.slice(5).replace(/^:\s?/, "").trimStart()); continue; }
          if (line.trim() === "") { flushEvent(); }
        }
      }
      flushEvent();

    } catch (e: any) {
      if (e?.name !== "AbortError") {
        console.error("Stream error:", e);
        if (e?.message === "Request failed with status code 401") logOut(dispatch);
        else if (e?.code === "ERR_NETWORK") showSnackbar("ERROR NETWORK", "error");
        else showSnackbar("Stream error", "error");
      }
    } finally {
      setIsStreaming(false);
      abortRef.current = null;
    }
  };

  // Рендер карточки блока
  const renderBlock = (block: BlockData) => {
    let BlockComponent: typeof BlockCard = BlockCard;
    let title = "" as string;
    let color: "primary" | "secondary" | "default" | "error" | "info" | "success" | "warning" = "default";
    switch (block.type) {
      case "user_prompt": BlockComponent = UserPromptCard; title = "Prompt"; color = "warning"; break;
      case "function_call": BlockComponent = FunctionCallCard; title = "Function Call"; color = "info"; break;
      case "function_result": BlockComponent = FunctionResultCard; title = "Function Result"; color = "success"; break;
      case "usage": BlockComponent = UsageCard; title = "Usage Statistics"; color = "primary"; break;
      case "a_history": BlockComponent = AHistoryCard; title = "Result by Conversations"; color = "secondary"; break;
    }
    return (
      <BlockComponent key={block.id}>
        <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Chip label={title} color={color} size="small" />
              {block.functionName && <Chip label={block.functionName} variant="outlined" size="small" color={color} />}
            </Box>
            <Typography variant="caption" color="text.secondary">{block.timestamp}</Typography>
          </Box>
          <Box sx={{ mt: 1, p: 1, bgcolor: "grey.50", borderRadius: 1, fontFamily: "monospace", fontSize: "0.875rem", overflow: "visible" }}>
            <pre style={{ margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-word", lineHeight: "1.4" }}>{formatJson(block.content)}</pre>
          </Box>
        </CardContent>
      </BlockComponent>
    );
  };

  // Кнопка Отправить
  const handleSendMessage = async () => {
    saveConfig();
    const userPrompt = prompt.trim();
    // prompt может быть пустым — отправляем даже с пустым prompt
    if (!chatConfig.aimodel) return;

    if (chatConfig.mode === "stream") {
      setPrompt("");
      await sendStreamMessage(userPrompt);
      return;
    }

    // Нестрим режим
    try {
      if (userPrompt) {
        setChatHistory((prev) => [...prev, { role: "user", content: userPrompt }]);
      }

      // conversations
      let conversations: string[] = [];
      try {
        const raw = localStorage.getItem("remembered_conversations") || "[]";
        const arr = JSON.parse(raw);
        if (Array.isArray(arr)) conversations = arr.map(String);
      } catch { /* noop */ }

      // history_b формируем локально
      const requestHistoryB: HistoryMessage[] = userPrompt
        ? [...chatHistory, { role: "user", content: userPrompt }]
        : [...chatHistory];

      const response = await axios.post(
        "https://cardgarantsales.com/restapi/conversationanalytics",
        [{
          aimodel: chatConfig.aimodel,
          conversations,
          aiprompt_a: chatConfig.aiprompt_a ? Number(chatConfig.aiprompt_a) : 0,
          aiprompt_b: chatConfig.aiprompt_b ? Number(chatConfig.aiprompt_b) : 0,
          prompt: userPrompt, // может быть пустым
          include_user_prompt: true,
          include_function_calls: true,
          include_function_outputs: true,
          history_b: requestHistoryB, // <--- отправляем историю в фазу B
          mode: "message",
        }],
        { headers: { "Content-Type": "application/json", Authorization: "Bearer " + localStorage.getItem("access_token"), } }
      );

      const botText = response.data?.assistant_message?.content
        ? response.data.assistant_message.content
        : (typeof response.data === 'string' ? response.data : JSON.stringify(response.data, null, 2));

      setChatHistory((prev) => [...prev, { role: "assistant", content: botText }]);
    } catch (error: any) {
      if (error.message === "Request failed with status code 401") logOut(dispatch);
      else if (error.code === "ERR_NETWORK") showSnackbar("ERROR NETWORK", "error");
      else showSnackbar("Ошибка отправки запроса", "error");
    }
  };

  // Справочники
  async function getAIModels(params: object = {}) {
    try {
      const response = await axios.get(`https://cardgarantsales.com/restapi/aimodels`, {
        headers: { "Content-Type": "application/json", Authorization: "Bearer " + localStorage.getItem("access_token"), },
        params,
      });
      setAImodels(response.data);
    } catch (error: any) {
      if (error.message === "Request failed with status code 401") logOut(dispatch);
      else if (error.code === "ERR_NETWORK") showSnackbar("ERROR NETWORK", "error");
    }
  }
  async function getAIPrompts(params: object = {}) {
    try {
      const response = await axios.get(`https://cardgarantsales.com/restapi/aiprompt`, {
        headers: { "Content-Type": "application/json", Authorization: "Bearer " + localStorage.getItem("access_token"), },
        params,
      });
      setAIprompts(response.data);
    } catch (error: any) {
      if (error.message === "Request failed with status code 401") logOut(dispatch);
      else if (error.code === "ERR_NETWORK") showSnackbar("ERROR NETWORK", "error");
    }
  }
  useEffect(() => { getAIModels(); getAIPrompts(); }, []);
  useEffect(() => { return () => { if (abortRef.current) try { abortRef.current.abort(); } catch { /* noop */ } }; }, []);

  // ---------------------------
  // Render
  // ---------------------------
  return (
    <Box sx={{ width: "100%", height: "88dvh", display: "flex", justifyContent: "center", p: 2, boxSizing: "border-box" }}>
      <ChatContainer>
        <MainContainer>
          <ChatSection>
            <MemoChatMessages messages={messages} parseHTML={parseHTML} chatRef={chatRef} />
            <InputContainer>
              <TextField
                sx={{ width: "100%", "& textarea": { resize: "vertical" } }}
                value={prompt}
                onChange={handleInputChange}
                onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSendMessage(); } }}
                label={<TranslatedText text={"Type your message..."} />}
                multiline rows={2} fullWidth variant="outlined"
              />
              <Button variant="contained" color="primary" onClick={handleSendMessage} disabled={isStreaming || !chatConfig.aimodel}>
                {isStreaming ? "Streaming..." : <TranslatedText text={"Send"} />}
              </Button>
            </InputContainer>
          </ChatSection>

          {/* Правый сайдбар с событиями */}
          <BlocksHeader collapsed={isBlocksCollapsed}>
            <IconButton size="small" onClick={() => setIsBlocksCollapsed(v => !v)} sx={{ bgcolor: "background.paper", boxShadow: 1, "&:hover": { bgcolor: "background.paper" } }}>
              {isBlocksCollapsed ? <ChevronLeftIcon /> : <ChevronRightIcon />}
            </IconButton>
          </BlocksHeader>

          <BlocksSection collapsed={isBlocksCollapsed}>
            <Collapse in={isBlocksCollapsed} timeout={0} unmountOnExit>
              <CollapsedBar elevation={0}>
                {blocks.length === 0 ? (
                  <Tooltip title={<TranslatedText text={"No activity blocks yet"} />}>
                    <Box sx={{ mt: 1, mb: 2, opacity: 0.6 }}><InboxIcon fontSize="small" /></Box>
                  </Tooltip>
                ) : (
                  blocks.map((b) => (
                    <Tooltip key={b.id} title={<Box sx={{ display: "flex", flexDirection: "column" }}><Typography variant="caption" sx={{ fontWeight: 600 }}>{b.type}</Typography><Typography variant="caption">{b.timestamp}</Typography></Box>} placement="right">
                      <IconButton size="small" onClick={() => setIsBlocksCollapsed(false)} aria-label={`open block ${b.type}`} sx={(theme) => ({ color: colorForBlockType(b.type, theme) })}>
                        {iconForBlockType(b.type)}
                      </IconButton>
                    </Tooltip>
                  ))
                )}
              </CollapsedBar>
            </Collapse>
            <Collapse in={!isBlocksCollapsed} timeout={0} unmountOnExit>
              <BlocksContainer elevation={0} ref={blocksRef}>
                {blocks.length === 0 ? (
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: "text.secondary" }}><TranslatedText text={"No activity blocks yet"} /></Box>
                ) : (
                  blocks.map((block, idx) => (
                    <Appear key={block.id} id={block.id} delay={Math.min(idx * 150, 240)}>
                      {renderBlock(block)}
                    </Appear>
                  ))
                )}
              </BlocksContainer>
            </Collapse>
          </BlocksSection>
        </MainContainer>

        {/* Нижняя панель управления */}
        <Stack direction="row" spacing={2} sx={{ px: 2, alignItems: "center", mt: 0 }}>
          <Tooltip title={<TranslatedText text={"AI Prompt A is launched separately for each conversation. (If not required, select Not Selected); AI Prompt B is launched once either based on the results after executing AI Prompt A, or on all conversations at once if AI Prompt A is not selected. AI Prompt is combined with the prompt from the text field. If AI Prompt B is not selected, the prompt from the text field is used instead."} />}>
          <IconButton color="primary">
            <HelpOutlineIcon />
          </IconButton>
          </Tooltip>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ ml: "auto", border: 1, borderRadius: 1, padding: 1, cursor: "pointer", userSelect: "none", minWidth: 180 }}
            onClick={handleOpenConversations}
            aria-haspopup="true"
            aria-expanded={Boolean(anchorElConversations)}
          >
            <TranslatedText text={'Conversations loaded'} />: {JSON.parse(localStorage.getItem("remembered_conversations") || "[]").length}
          </Typography>

          <Popover
            open={Boolean(anchorElConversations)}
            anchorEl={anchorElConversations}
            onClose={handleCloseConversations}
            transformOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            PaperProps={{ sx: { p: 2, maxWidth: 320 } }}
          >
            <Box>
              {JSON.parse(localStorage.getItem("remembered_conversations") || "[]").length == 0 ?
                <Box >
                  <Typography sx={{ mb: 2 }}><TranslatedText text={"List of conversations for analytics is empty. Go to the conversations page and select the conversations you need for analytics."} /></Typography>
                  <Link to={`/conversations`} style={{ width: '100%' }}>
                    <Button variant="outlined" size="small" ><TranslatedText text={"Go to the conversations"} /></Button>
                  </Link>
                </Box>
                : JSON.parse(localStorage.getItem("remembered_conversations") || "[]").map((convId: string, idx: number) => (
                  <Link key={idx} to={`/conversationdetails?uniqueid=${convId}`} style={{ textDecoration: 'none' }}>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                      <Typography variant="body2" sx={{ wordBreak: "break-all" }}>{convId}</Typography>
                    </Box>
                  </Link>)
                )}
            </Box>
          </Popover>

          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel id="ai-model-select"><TranslatedText text={"AI Model"} /></InputLabel>
            <Select labelId="ai-model-select" value={chatConfig.aimodel} label={<TranslatedText text={"AI Model"} />} onChange={(e) => setChatConfig({ ...chatConfig, aimodel: e.target.value })}>
              {aimodels.map((el) => (<MenuItem key={el.id} value={el.name}>{el.name}</MenuItem>))}
            </Select>
          </FormControl>

          {/* ДВА промпта: A (по каждому разговору) и B (по всем разговорам) */}

          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel id="ai-prompt-a-select"><TranslatedText text={"AI Prompt A"} /></InputLabel>
            <Select labelId="ai-prompt-a-select" value={chatConfig.aiprompt_a} label={<TranslatedText text={"AI Prompt A"} />} onChange={(e) => setChatConfig({ ...chatConfig, aiprompt_a: e.target.value })}>
              <MenuItem value={""}><TranslatedText text={"Not Selected"} /></MenuItem>
              {aiprompts.map((el: any) => (<MenuItem key={el.id} value={el.id}>{el.code}</MenuItem>))}
            </Select>
          </FormControl>


            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel id="ai-prompt-b-select"><TranslatedText text={"AI Prompt B"} /></InputLabel>
              <Select labelId="ai-prompt-b-select" value={chatConfig.aiprompt_b} label={<TranslatedText text={"AI Prompt B"} />} onChange={(e) => setChatConfig({ ...chatConfig, aiprompt_b: e.target.value })}>
                <MenuItem value={""}><TranslatedText text={"Not Selected"} /></MenuItem>
                {aiprompts.map((el: any) => (<MenuItem key={el.id} value={el.id}>{el.code}</MenuItem>))}
              </Select>
            </FormControl>

          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography>HTML</Typography>
            <Switch onChange={() => setParseHTML(!parseHTML)} checked={parseHTML} />
          </Box>
          <Button variant="text" color="error" size="small" onClick={handleClearHistory} disabled={isStreaming}><TranslatedText text={"Clear chat"} /></Button>
          
        </Stack>
      </ChatContainer>
    </Box>
  );
}
